import { useEffect, useState } from 'react';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import { BundlesTable, toTableSections } from './subscriptionBundle.vm.mapper';
import {
  mapBundlesToInput,
  mapToggledContent,
  ToggleSubscriptionBundlePayload,
  validatePlatformBundleSubset,
} from '../model/bundle.model.mapper';
import { trackEvent } from '@services/mixpanel';
import { ButtonStylingProps } from '@design-system/components/button/helper';
import { bundleUpdateValidationToast } from '../../view/BundleUpdateValidationToast';
import { SubscriptionBundle } from '../../common/bundle.types';
import toast from 'react-hot-toast';
import { BundlesModel } from '../model/subscriptionBundles.model';
import { PATHS } from '@client/routes';
import { isEmpty } from 'lodash';
import { ConfirmPublishModalProps } from '../../common/ConfirmPublishModal';
import { BundleItemType } from 'generated/graphql';
import { toNewlyAddedDraftProgramNames } from './editBundleContent.vm.mappers';

export type EditBundleContentVM = {
  list: {
    data: BundlesTable;
    cta: Array<{
      label: string;
      onClick: () => void;
      variant?: ButtonStylingProps['variant'];
      disabled: boolean;
    }>;
    searchText: string;
    search: (value: string) => void;
  };
  confirmSaveModal: ConfirmPublishModalProps;
};

type PayloadMap = {
  search: string;
  toggleListSection: null;
  toggleProgramsSection: null;
  toggleFeaturesSection: null;
  toggleBundleContent: ToggleSubscriptionBundlePayload;
  cancelContentEdit: null;
  saveContentChanges: null;
  confirmSaveContentChanges: null;
};

export type EditBundleContentVMAction = Actions<PayloadMap>;
export type EditBundleContentVMDispatcher = FSpread<PayloadMap>;

export type EditBundleContentVMState = {
  searchText: string;
  listSectionOpen: boolean;
  programsSectionOpen: boolean;
  featuresSectionsOpen: boolean;
  contentSectionsOpen: boolean;
  isSubmittingContent: boolean;
  isDirty: boolean;
  subscriptionBundles: SubscriptionBundle[] | null;
  confirmDraftItems: string[];
};

const warnBeforeUnload = (ev: BeforeUnloadEvent) => {
  ev.preventDefault();
  ev.returnValue = '';
};

export const useEditBundleContentVM = ({
  model,
}: {
  model: Async<BundlesModel>;
}): EditBundleContentVM => {
  const [state, setState] = useState<EditBundleContentVMState>({
    searchText: '',
    listSectionOpen: false,
    programsSectionOpen: true,
    featuresSectionsOpen: true,
    contentSectionsOpen: true,
    isSubmittingContent: false,
    isDirty: false,
    subscriptionBundles: null,
    confirmDraftItems: [],
  });

  useEffect(() => {
    state.isDirty
      ? window.addEventListener('beforeunload', warnBeforeUnload)
      : window.removeEventListener('beforeunload', warnBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload);
    };
  }, [state.isDirty]);

  const shouldUpdateSubscriptionBundles =
    model.type === 'fetched' &&
    !isEmpty(model.value.initialSubscriptionBundles);

  useEffect(() => {
    if (model.type === 'fetched' && !state.subscriptionBundles) {
      setState((prev) => ({
        ...prev,
        subscriptionBundles: model.value.initialSubscriptionBundles,
      }));
    }
  }, [shouldUpdateSubscriptionBundles]);

  const modelValue = model.type === 'fetched' ? model.value : null;
  const initialSubscriptionBundles =
    modelValue?.initialSubscriptionBundles || [];

  const dispatch: EditBundleContentVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as EditBundleContentVMAction;
    switch (action.type) {
      case 'search':
        setState((prev) => ({ ...prev, searchText: action.payload }));
        return null;
      case 'toggleListSection':
        setState((prev) => ({
          ...prev,
          listSectionOpen: !prev.listSectionOpen,
        }));
        return null;
      case 'toggleProgramsSection':
        setState((prev) => ({
          ...prev,
          programsSectionOpen: !prev.programsSectionOpen,
        }));
        return null;
      case 'toggleFeaturesSection':
        setState((prev) => ({
          ...prev,
          featuresSectionsOpen: !prev.featuresSectionsOpen,
        }));
        return null;
      case 'cancelContentEdit':
        setState((prev) => ({
          ...prev,
          isDirty: false,
          subscriptionBundles: initialSubscriptionBundles,
        }));
        return null;
      case 'saveContentChanges': {
        const draftNames = toNewlyAddedDraftProgramNames({
          currentBundles: state.subscriptionBundles || [],
          initialBundles: initialSubscriptionBundles,
          subscriptionPrograms: modelValue?.subscriptionPrograms || [],
        });
        if (!isEmpty(draftNames)) {
          setState((prev) => ({ ...prev, confirmDraftItems: draftNames }));
          return null;
        }
        executeSaveContentChanges();
        return null;
      }
      case 'confirmSaveContentChanges':
        setState((prev) => ({ ...prev, confirmDraftItems: [] }));
        executeSaveContentChanges();
        return null;
      case 'toggleBundleContent':
        setBundleContentChange(action.payload);
        return null;
    }
  };

  const executeSaveContentChanges = () => {
    setState((prev) => ({
      ...prev,
      isSubmittingContent: true,
    }));

    const input = mapBundlesToInput(state.subscriptionBundles || []);
    modelValue
      ?.updateBundleContent(input)
      .then(() => {
        toast.success('Subscription offers were updated successfully');
        trackEvent({
          type: 'Subscription offer updated',
        });
      })
      .catch((e) => {
        toast.error(e.message);
        setState((prev) => ({
          ...prev,
          subscriptionBundles: initialSubscriptionBundles,
        }));
      })
      .finally(() => {
        setState((prev) => ({
          ...prev,
          isSubmittingContent: false,
          isDirty: false,
        }));
        modelValue?.refetch();
        modelValue?.refetchInfluencer();
      });
  };
  const setBundleContentChange = (payload: ToggleSubscriptionBundlePayload) => {
    const mappedBundles = mapToggledContent(
      state.subscriptionBundles || [],
      payload,
    );
    const hasError = validatePlatformBundleSubset(mappedBundles);
    if (hasError) {
      bundleUpdateValidationToast();
      return;
    }

    const isDirty =
      JSON.stringify(initialSubscriptionBundles) !==
      JSON.stringify(mappedBundles);

    setState((prev) => ({
      ...prev,
      isDirty,
      subscriptionBundles: mappedBundles,
    }));
  };

  const filteredSubscriptionPrograms = (
    modelValue?.subscriptionPrograms || []
  ).filter((sp) =>
    sp.name.toLocaleLowerCase().includes(state.searchText.toLocaleLowerCase()),
  );
  const filteredAllFeatures = (modelValue?.allFeatures || []).filter((f) =>
    f.name.toLocaleLowerCase().includes(state.searchText.toLocaleLowerCase()),
  );
  const filteredAllContent = (modelValue?.sellableContent || []).filter((f) =>
    f.name.toLocaleLowerCase().includes(state.searchText.toLocaleLowerCase()),
  );

  const links = {
    extrasPath: PATHS.EXTRAS(modelValue?.creatorId || 0),
    programsPath: modelValue?.isUnifiedProgramsEnabled
      ? PATHS.PROGRAMS(modelValue?.creatorId || 0)
      : PATHS.SUBSCRIPTIONS(modelValue?.creatorId || 0),
  };

  return {
    list: {
      data: toTableSections(
        dispatch,
        state,
        filteredSubscriptionPrograms,
        filteredAllFeatures,
        filteredAllContent,
        state.subscriptionBundles || [],
        links,
        modelValue?.isUnifiedProgramsEnabled ?? false,
      ),
      searchText: state.searchText,
      search: (value) => dispatch('search', value),
      cta: toListCta(state.isDirty, state.isSubmittingContent, dispatch),
    },
    confirmSaveModal: {
      isOpen: !isEmpty(state.confirmDraftItems),
      confirm: () => dispatch('confirmSaveContentChanges'),
      cancel: () => setState((prev) => ({ ...prev, confirmDraftItems: [] })),
      draftItems: state.confirmDraftItems,
    },
  };
};

const toListCta = (
  isDirty: boolean,
  isSubmitting: boolean,
  dispatch: EditBundleContentVMDispatcher,
) => {
  if (!isDirty) return [];

  return [
    {
      label: 'Cancel',
      onClick: () => dispatch('cancelContentEdit'),
      variant: 'text' as ButtonStylingProps['variant'],
      disabled: isSubmitting,
    },
    {
      label: 'Save',
      onClick: () => dispatch('saveContentChanges'),
      disabled: isSubmitting,
    },
  ];
};
